import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ListChecks, Minus, X, Maximize2, AlertCircle, RefreshCw, Lock, GripHorizontal } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { capturePersonalCacheBoundary } from "@/lib/personal-cache-boundary";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

export interface ChecklistItem {
  id?: string;
  text: string;
  checked: boolean;
  checkedBy?: string | null;
  checkedAt?: string | null;
  checkedByName?: string | null;
}

export interface ReadableChecklist {
  id: string;
  name: string;
  items: ChecklistItem[];
  access: "owner" | "edit" | "view";
  updatedAt: string;
}

export const FLOATING_CHECKLIST_ATTR = "data-floating-checklist";

/** Use in a task dialog's onInteractOutside so clicks in the floating checklist never dismiss it. */
export function isFloatingChecklistTarget(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest(`[${FLOATING_CHECKLIST_ATTR}]`);
}

export function checklistQueryKey(id: string) {
  return ["/api/checklists", id] as const;
}

/** Build the PATCH payload for toggling a single item; exported for tests. */
export function buildToggleChecklistPayload(current: ReadableChecklist, index: number) {
  return {
    items: current.items.map((it, i) => (i === index ? { ...it, checked: !it.checked } : it)),
    expectedUpdatedAt: current.updatedAt,
  };
}

interface WorkspaceValue {
  openChecklist: (id: string) => void;
  closeChecklist: () => void;
  isChecklistOpen: boolean;
  openChecklistId: string | null;
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function useTaskChecklistWorkspace(): WorkspaceValue {
  const ctx = useContext(WorkspaceContext);
  // Safe fallback so task dialogs still render outside the provider (e.g. tests).
  return ctx ?? { openChecklist: () => {}, closeChecklist: () => {}, isChecklistOpen: false, openChecklistId: null };
}

export function TaskChecklistWorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [opened, setOpened] = useState<{ id: string; userId: string } | null>(null);
  const openId = opened?.userId === userId ? opened.id : null;
  const [minimized, setMinimized] = useState(false);
  const ownerRef = useRef<string | null>(userId);

  // Clear on logout or account switch, and drop cached private checklist data.
  useEffect(() => {
    if (ownerRef.current !== userId) {
      ownerRef.current = userId;
      setOpened(null);
      setMinimized(false);
      queryClient.removeQueries({ queryKey: ["/api/checklists"] });
    }
  }, [userId]);

  const openChecklist = useCallback((id: string) => {
    if (!userId) return;
    setOpened({ id, userId });
    setMinimized(false);
  }, [userId]);
  const closeChecklist = useCallback(() => setOpened(null), []);

  const value = useMemo<WorkspaceValue>(() => ({
    openChecklist, closeChecklist, isChecklistOpen: !!openId && !!userId, openChecklistId: openId,
  }), [openChecklist, closeChecklist, openId, userId]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
      {openId && userId && typeof document !== "undefined" &&
        createPortal(
          <FloatingChecklistWindow
            key={`${userId}:${openId}`}
            checklistId={openId}
            minimized={minimized}
            onMinimize={() => setMinimized(true)}
            onRestore={() => setMinimized(false)}
            onClose={closeChecklist}
          />,
          document.body,
        )}
    </WorkspaceContext.Provider>
  );
}

const WIN_W = 360;
const MARGIN = 8;

function clampPos(x: number, y: number, w: number, h: number) {
  const maxX = Math.max(MARGIN, window.innerWidth - w - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - h - MARGIN);
  return { x: Math.min(Math.max(MARGIN, x), maxX), y: Math.min(Math.max(MARGIN, y), maxY) };
}

export function FloatingChecklistWindow({
  checklistId, minimized, onMinimize, onRestore, onClose,
}: {
  checklistId: string;
  minimized: boolean;
  onMinimize: () => void;
  onRestore: () => void;
  onClose: () => void;
}) {
  const winRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(() => {
    const w = Math.min(WIN_W, window.innerWidth - MARGIN * 2);
    return clampPos(window.innerWidth - w - 24, 88, w, 200);
  });
  const drag = useRef<{ dx: number; dy: number; id: number } | null>(null);
  const [conflict, setConflict] = useState(false);
  const [deniedStatus, setDeniedStatus] = useState<number | undefined>();
  const mounted = useRef(true);
  const saving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const query = useQuery<ReadableChecklist>({
    queryKey: checklistQueryKey(checklistId),
    queryFn: async ({ signal }) => {
      const res = await apiRequest("GET", `/api/checklists/${checklistId}`, undefined, undefined, { signal });
      return res.json();
    },
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    retry: (count, err: any) => err?.status !== 403 && err?.status !== 404 && count < 2,
  });

  const status = deniedStatus ?? (query.error as any)?.status as number | undefined;
  const revoked = status === 401 || status === 403 || status === 404;
  // Never show cached private content after a revoke / delete.
  const checklist = revoked ? undefined : query.data;

  useEffect(() => {
    if (revoked) queryClient.removeQueries({ queryKey: checklistQueryKey(checklistId), exact: true, type: "inactive" });
  }, [revoked, checklistId]);

  const toggle = useMutation({
    onMutate: () => ({ isCurrentSession: capturePersonalCacheBoundary() }),
    mutationFn: async (index: number) => {
      const current = queryClient.getQueryData<ReadableChecklist>(checklistQueryKey(checklistId));
      if (!current) throw new Error("Checklist not loaded");
      const res = await apiRequest("PATCH", `/api/checklists/${checklistId}`, buildToggleChecklistPayload(current, index));
      return (await res.json()) as ReadableChecklist;
    },
    onSuccess: (updated, _index, context) => {
      if (!mounted.current || !context?.isCurrentSession()) return;
      setConflict(false);
      queryClient.setQueryData<ReadableChecklist>(checklistQueryKey(checklistId), (old) =>
        old ? { ...old, ...updated, access: updated.access ?? old.access } : updated);
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"], exact: true });
    },
    onError: (err: any) => {
      if (!mounted.current) return;
      if (err?.status === 409) setConflict(true);
      if ([401, 403, 404].includes(err?.status)) setDeniedStatus(err.status);
      queryClient.invalidateQueries({ queryKey: checklistQueryKey(checklistId) });
    },
    onSettled: () => { saving.current = false; },
  });
  useEffect(() => {
    if (query.isSuccess) setDeniedStatus(undefined);
  }, [query.dataUpdatedAt, query.isSuccess]);

  // Keep the window inside the viewport on resize.
  useEffect(() => {
    const onResize = () => {
      const el = winRef.current;
      setPos((p) => {
        const next = clampPos(p.x, p.y, el?.offsetWidth ?? WIN_W, el?.offsetHeight ?? 200);
        return next.x === p.x && next.y === p.y ? p : next;
      });
    };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    if (winRef.current) observer?.observe(winRef.current);
    window.addEventListener("resize", onResize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as Element).closest("button, a, input, [data-no-drag]")) return;
    if (e.button !== 0) return;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, id: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current || drag.current.id !== e.pointerId) return;
    const el = winRef.current;
    setPos(clampPos(e.clientX - drag.current.dx, e.clientY - drag.current.dy, el?.offsetWidth ?? WIN_W, el?.offsetHeight ?? 200));
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === e.pointerId) {
      drag.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const items = checklist?.items ?? [];
  const done = items.filter((i) => i.checked).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  const readOnly = checklist?.access !== "owner" && checklist?.access !== "edit";
  const title = checklist?.name ?? (revoked ? "Checklist unavailable" : "Checklist");

  return (
    <div
      ref={winRef}
      {...{ [FLOATING_CHECKLIST_ATTR]: "" }}
      role="region"
      aria-label={`Checklist: ${title}`}
      data-testid="floating-checklist"
      className="fixed z-[60] flex flex-col rounded-xl border bg-card text-card-foreground shadow-2xl pointer-events-auto"
      style={{ left: pos.x, top: pos.y, width: `min(${WIN_W}px, calc(100vw - ${MARGIN * 2}px))`, maxHeight: "min(70vh, 560px)" }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b bg-muted/60 rounded-t-xl cursor-grab active:cursor-grabbing select-none touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="floating-checklist-header"
      >
        <GripHorizontal className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        <ListChecks className="h-4 w-4 text-primary shrink-0" aria-hidden />
        <span className="flex-1 truncate text-sm font-semibold" data-testid="text-floating-checklist-name">{title}</span>
        {checklist && <span className="text-xs text-muted-foreground tabular-nums">{done}/{items.length}</span>}
        {minimized ? (
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onRestore} aria-label="Restore checklist window" data-testid="button-restore-checklist">
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onMinimize} aria-label="Minimize checklist window" data-testid="button-minimize-checklist">
            <Minus className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close checklist window" data-testid="button-close-checklist">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {!minimized && (
        <div className="flex min-h-0 flex-col">
          {query.isLoading && (
            <div className="space-y-2 p-3" aria-busy="true" aria-label="Loading checklist">
              <Skeleton className="h-2 w-full" />
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-7 w-full" />)}
            </div>
          )}

          {revoked && (
            <div className="flex flex-col items-center gap-2 p-6 text-center" role="alert" data-testid="status-checklist-revoked">
              <Lock className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">{status === 404 ? "This checklist no longer exists." : "You no longer have access to this checklist."}</p>
              <Button type="button" size="sm" variant="outline" onClick={onClose}>Close</Button>
            </div>
          )}

          {query.isError && !revoked && !checklist && (
            <div className="flex flex-col items-center gap-2 p-6 text-center" role="alert" data-testid="status-checklist-error">
              <AlertCircle className="h-6 w-6 text-destructive" />
              <p className="text-sm">Couldn't load this checklist.</p>
              <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()} data-testid="button-retry-checklist">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          )}

          {checklist && (
            <>
              <div className="px-3 pt-3 pb-2 space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{pct}% complete</span>
                  {readOnly && <span className="inline-flex items-center gap-1" data-testid="status-checklist-view-only"><Lock className="h-3 w-3" /> View only</span>}
                </div>
                <Progress value={pct} className="h-1.5" aria-label={`${done} of ${items.length} items checked`} />
              </div>
              {conflict && (
                <div className="mx-3 mb-2 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs" role="alert" data-testid="status-checklist-conflict">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
                  <span className="flex-1">Someone else changed this checklist. We loaded the latest version — please re-apply your change.</span>
                  <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setConflict(false)} aria-label="Dismiss conflict message"><X className="h-3 w-3" /></button>
                </div>
              )}
              {toggle.isError && !conflict && !revoked && (
                <p className="mx-3 mb-2 text-xs text-destructive" role="alert">
                  Couldn't confirm this change was saved. Check the refreshed checklist before trying again.
                </p>
              )}
              {items.length === 0 ? (
                <p className="px-3 pb-4 text-sm text-muted-foreground text-center" data-testid="status-checklist-empty">This checklist has no items yet.</p>
              ) : (
                <ul className="overflow-y-auto px-2 pb-2 min-h-0" style={{ maxHeight: "calc(min(70vh, 560px) - 110px)" }} aria-label="Checklist items">
                  {items.map((item, i) => {
                    const id = `fc-${checklistId}-${i}`;
                    return (
                      <li key={i} className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50">
                        <input
                          id={id}
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 accent-[hsl(213,92%,48%)] disabled:cursor-not-allowed"
                          checked={item.checked}
                          disabled={readOnly || toggle.isPending}
                          onChange={() => {
                            if (saving.current || readOnly || revoked) return;
                            saving.current = true;
                            toggle.mutate(i);
                          }}
                          data-testid={`checkbox-floating-checklist-item-${i}`}
                        />
                        <label htmlFor={id} className={`flex-1 text-sm break-words ${item.checked ? "line-through text-muted-foreground" : ""} ${readOnly ? "cursor-default" : "cursor-pointer"}`}>
                          {item.text}
                          {item.checked && item.checkedByName && (
                            <span className="block text-[11px] text-muted-foreground no-underline">by {item.checkedByName}</span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
